/**
 * Format an address object or string for display
 * Handles both string addresses and address objects with line1, city, postcode properties
 * or regular address, city, postcode properties
 * 
 * @param {(string|Object)} address - The address to format
 * @param {string} [fallback='Unknown Property'] - Fallback text if address is empty or invalid
 * @returns {string} The formatted address as a string
 */
export const formatAddress = (address, fallback = 'Unknown Property') => {
  if (!address) return fallback;
  
  // If address is already a string, return it
  if (typeof address === 'string') return address;
  
  // Make sure we're handling an object
  if (typeof address !== 'object') {
    console.warn('Address is not a string or an object:', address);
    return fallback;
  }
  
  try {
    // If address is an object with line1, city, postcode properties
    const parts = [];
    
    // Try different property names that might be in the address object
    if (address.line1) parts.push(address.line1);
    else if (address.address) parts.push(address.address);
    
    if (address.city) parts.push(address.city);
    
    if (address.postcode) parts.push(address.postcode);
    else if (address.zip) parts.push(address.zip);
    else if (address.zipCode) parts.push(address.zipCode);
    else if (address.postalCode) parts.push(address.postalCode);
    
    // If parts array is empty, try to use the address as a whole if it has a toString method
    if (parts.length === 0 && address.toString && typeof address.toString === 'function' && address.toString() !== '[object Object]') {
      return address.toString();
    }
    
    return parts.length > 0 ? parts.join(', ') : fallback;
  } catch (error) {
    console.error('Error formatting address:', error, address);
    return fallback;
  }
};

/**
 * Safely formats an address with multiple fallback options
 * @param {Object|string} address - The address object or string
 * @param {string} fallback - Optional fallback string if formatting fails
 * @returns {string} - Safely formatted address string
 */
export const safeFormatAddress = (address, fallback = 'Unknown Property') => {
  try {
    // If it's falsy, early return
    if (!address) return fallback;

    // If it's already a string, return it directly
    if (typeof address === 'string') return address;
    
    // Make sure we're dealing with an object
    if (typeof address !== 'object') {
      return String(address);
    }
    
    // Check for React elements or arrays - cannot be rendered directly
    if (
      (typeof window !== 'undefined' && window.React && window.React.isValidElement(address)) || 
      Array.isArray(address)
    ) {
      return 'Complex Address Object';
    }

    // Strategy 1: Use formatAddress from utils
    try {
      const formatted = formatAddress(address);
      if (formatted && formatted !== 'Unknown Property') {
        return formatted;
      }
    } catch (err) {
      console.warn('formatAddress failed:', err);
    }
    
    // Strategy 2: Extract common address fields
    try {
      const parts = [];
      
      // Handle line1, city, postcode format
      if (address.line1) parts.push(address.line1);
      if (address.city) parts.push(address.city);
      if (address.postcode) parts.push(address.postcode);
      
      // Handle street, city, zip format
      if (address.street && !parts.includes(address.street)) parts.push(address.street);
      if (address.town && !parts.includes(address.town)) parts.push(address.town);
      if (address.zip && !parts.includes(address.zip)) parts.push(address.zip);
      
      // Try using the address field directly if it's a string
      if (address.address && typeof address.address === 'string') {
        parts.push(address.address);
      }
      
      if (parts.length > 0) {
        return parts.join(', ');
      }
    } catch (err) {
      console.warn('Common field extraction failed:', err);
    }
    
    // Strategy 3: Use toString if available
    try {
      if (typeof address.toString === 'function' && address.toString() !== '[object Object]') {
        return address.toString();
      }
    } catch (err) {
      console.warn('toString failed:', err);
    }
    
    // Strategy 4: Stringify the object as a last resort
    try {
      return JSON.stringify(address);
    } catch (err) {
      console.warn('JSON.stringify failed:', err);
    }
    
    // Ultimate fallback
    return fallback;
  } catch (error) {
    console.error('Error in safeFormatAddress:', error);
    return fallback;
  }
};

// ponytail: O(n) scan of loaded properties; index by id if the list gets huge
export const displayMaintenancePropertyAddress = (request, properties = []) => {
  const raw = request?.propertyAddress
    || properties.find((p) => p.id === request?.propertyId)?.address;
  return safeFormatAddress(raw, 'Property not found');
}; 